import React from 'react';
import { CircularProgress, Badge } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { AppContext } from '../../../App';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../../errorResponses';
import { ITrainingRevisionCreateParams, ITrainingSetUpdateParams } from '../../../api/types';

import useGlynt from '../../hooks/useGlynt';
import { ITrainingSet } from '../../../types/my-types';

import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';

import { randomStr } from '../../../utils/randomStr';
import { MIN_TRAIN_DOCS, LABEL_LIMIT } from '../../../constants';

import { AxiosError } from 'axios';

import { useTrainingSetUpdate } from '../../hooks/useTrainingSetUpdate';

const generateLabel = (trainingSet: ITrainingSet) => {
  if (trainingSet.label.length < LABEL_LIMIT - 7) {
    return `${trainingSet!.label}-${randomStr()}`;
  } else {
    return `${trainingSet.label.substr(0, LABEL_LIMIT - 7)}-${randomStr()}`;
  }
};

interface IProps {
  trainingSet: ITrainingSet;
  type: 'menu' | 'icon';
  isTrainingInProgress?: boolean;
  handleClose?: () => void;
}

function TrainingRevisionCreate({ trainingSet, type, isTrainingInProgress, handleClose }: IProps) {
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();

  const [disabled, setDisabled] = React.useState(false);
  const [coreVersion, setCoreVersion] = React.useState('');

  const createTrainingRevision = useMutation({
    mutationFn: async (data: ITrainingRevisionCreateParams) => (await glynt).trainingRevision.create(data),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingRevisions']
      });

      setToast({
        variant: 'success',
        open: true,
        message: 'Training started successfully.',
      });
    },

    onError: (error: AxiosError) => {
      let message = error.message || errorResponses.failedToCreateTrainingRevision;
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    }
  });

  const updateTrainingSet = useTrainingSetUpdate({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingSet']
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToUpdateTrainingSet,
      });
    },
  });

  const handleTrain = async (benchmark: boolean) => {
    const label = generateLabel(trainingSet!);

    setDisabled(true);
    if (trainingSet!.documents.length < MIN_TRAIN_DOCS && benchmark) {
      setToast({
        variant: 'error',
        open: true,
        message: `Sorry, failed to train with benchmark. Please make sure you have at least ${MIN_TRAIN_DOCS} marked documents in training set. Or try train without benchmark.`,
      });

      setDisabled(false);
    } else {
      try {
        createTrainingRevision.mutate({
          training_set: trainingSet!.id,
          label,
          benchmark,
          core_version: coreVersion,
        });
        setDisabled(false);
      } catch (error) {
        setToast({
          variant: 'error',
          open: true,
          message: error,
        });
        setDisabled(false);
      }
    }
  };

  const { data: groundTruths, isPending: loadingGroundTruths } = useQuery({
    queryKey: ['groundTruths', trainingSet.id],

    queryFn: async () =>
      (await glynt).groundTruths.list({
        advanced: true,
        training_set: trainingSet.id,
      }),

    placeholderData: keepPreviousData
  });

  const handleSubmit = async () => {
    await handleTrain(false);
    updateTrainingSet.mutate({
      trainingSetId: trainingSet.id,
      data: { training_requested: false },
    });
    runHandleClose();
  };

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const notAllowTrainReasons: string[] = [];

  if (trainingSet.fields.length === 0) {
    notAllowTrainReasons.push('No fields.');
  }
  if (trainingSet?.documents.length === 0) {
    notAllowTrainReasons.push('No documents.');
  }
  if (groundTruths?.count === 0) {
    notAllowTrainReasons.push('No ground truths.');
  }

  const notAllowTrain = notAllowTrainReasons.length > 0;

  const trainingErrorMessage = `Cannot Train: ${notAllowTrainReasons.join(' ')}`;

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          key={'Review'}
          data-cy={`btn-create-training-revision-${trainingSet.label}`}
          disabled={createTrainingRevision.isPending || disabled || notAllowTrain}
          onClick={() => handleSubmit()}
          onAuxClick={handleClose}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
        >
          <ListItemIcon>
            {createTrainingRevision.isPending ? (
              <CircularProgress size={15} />
            ) : (
              <ModelTrainingIcon fontSize="small" />
            )}
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Train</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <Badge
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          overlap="rectangular"
          invisible={!trainingSet.trained}
          sx={{
            '& .MuiBadge-badge': {
              right: 2,
            },
          }}
          badgeContent={
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#65BBA9',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.15)',
              }}
            >
              <DoneIcon sx={{ fontSize: 10, color: '#ffffff' }} />
            </span>
          }
        >
          <IconButtonWhiteBg
            disabled={createTrainingRevision.isPending || disabled || notAllowTrain}
            onClick={() => handleSubmit()}
            id="icon-create-training-revision"
            cypressId={`icon-create-training-revision`}
            tooltip={isTrainingInProgress ? 'Training in progress' : notAllowTrain ? trainingErrorMessage : 'Train'}
          >
            {createTrainingRevision.isPending ? (
              <CircularProgress size={15} />
            ) : isTrainingInProgress ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <ModelTrainingIcon style={{ fontSize: '15px', color: 'black' }} />
            )}
          </IconButtonWhiteBg>
        </Badge>
      ) : null}
    </>
  );
}

export default TrainingRevisionCreate;
